import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, SxProps, Theme, Typography } from '@mui/material';
import { isPostAsset } from 'common/type-guards';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { FieldArrayWithId, UseFieldArrayReturn } from 'react-hook-form';
import { ProductId } from 'store/api/pages-api/pages-api-types';

import { CommonPageForm } from '../../../../../../../page-builder-v2.types';
import { ProductPost, ProductPostCreator } from './components';
import { styles } from './product-post-form.styles';

type Props = {
  assetFields: UseFieldArrayReturn<CommonPageForm, `products.${number}.productAssets`>
  currentProductId: ProductId;
  open: boolean;
  onClose: () => void;
  productIdx: number;
};

const ProductPostForm = ({ assetFields, currentProductId, onClose, open, productIdx }: Props) => {
  const { append, fields, move, remove, update } = assetFields;
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  const onDeleteButton = (
    assetIdx: number,
    asset: FieldArrayWithId<CommonPageForm, `products.${number}.productAssets`>,
  ) => {
    if (asset.type === 'deleted') return;
    if (asset.type === 'added') {
      remove(assetIdx);
    } else {
      update(assetIdx, { ...asset, type: 'deleted' });
    }
  };

  const visibleItems = fields.flatMap((asset, assetIdx) =>
    isPostAsset(asset) && asset.type !== 'deleted' ? [{ asset, assetIdx }] : [],
  );

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = visibleItems[result.source.index].assetIdx;
    const to = visibleItems[result.destination.index].assetIdx;
    if (from !== to) move(from, to);
  };

  if (!enabled) return null;

  return (
    <Box sx={styles.wrapper}>
      <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open}>
        <DialogTitle sx={styles.dialogTitle}>
          Add Post
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <ProductPostCreator
            currentProductId={currentProductId}
            onAddButton={(data) => {
              append(data);
              onClose();
            }}
          />
        </DialogContent>
      </Dialog>

      {visibleItems.length > 0 ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable direction="horizontal" droppableId="posts-droppable">
            {(provided) => (
              <Box ref={provided.innerRef} sx={styles.list} {...provided.droppableProps}>
                {visibleItems.map(({ asset, assetIdx }, index) => (
                  <Draggable key={asset.id} draggableId={asset.id} index={index}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={provided.draggableProps.style}
                        sx={[styles.cardWrapper, snapshot.isDragging && styles.cardWrapperDragging] as SxProps<Theme>}
                      >
                        <ProductPost
                          asset={asset}
                          assetIdx={assetIdx}
                          onDeleteButton={() => onDeleteButton(assetIdx, asset)}
                          productIdx={productIdx}
                        />
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Box sx={styles.emptyState}>
          <ArticleOutlinedIcon sx={{ fontSize: 36 }} />
          <Typography variant="body2">No posts added yet</Typography>
        </Box>
      )}
    </Box>
  );
};

export { ProductPostForm };
